export const BRAND = {
  wordmark: "/brand/wordmark-red-t.png",
  wordmarkDark: "/brand/wordmark-dark-t.png",
  monogram: "/brand/monogram-red-t.png",
  urdu: "اَن زِپ دِلّی",
  star: "/brand/star-complete.png",
  border: "/brand/border-complete.png",
};

export const ART = {
  courtyard: "/brand/archive-14.jpg",
  jamaMasjid: "/brand/jama-masjid-painting.jpg",
  gateway: "/brand/archive-13.jpg",
  shrine: "/brand/archive-15.jpg",
  oldFort: "/brand/archive-16.jpg",
  jeweller: "/brand/jeweller-painting.jpg",
  cityMap: "/brand/delhi-map.jpg",
  kotla: "/brand/kotla-painting.jpg",
  archway: "/brand/archway-archive.jpg",
  cityGates: "/brand/city-gates.jpg",
  qutub: "/brand/qutub-archive-portrait.jpg",
  qutubWide: "/brand/qutub-archive-wide.jpg",
  founder: "/brand/anas-khan.webp",
};

export const CONTACT = {
  email: "unzip.delhi@gmail.com",
  phone: "+91 79828 39895",
  whatsapp: "https://wa.me/917982839895",
  instagram: "@unzip_delhi",
  instagramUrl: "https://www.instagram.com/unzip_delhi/",
  youtubeUrl: "https://youtube.com/@unzip_delhi",
  city: "New Delhi, India",
  company: "Unzip Delhi",
  founder: "Anas Khan",
};

export const EXPERIENCES = [
  {
    id: "shahjahanabad",
    name: "Old Delhi / Shahjahanabad",
    title: "The city behind the chaos.",
    description: "Explore the streets, markets, food, architecture and stories of Shahjahanabad, from Chandni Chowk and Dariba Kalan to Khari Baoli and Jama Masjid.",
    image: ART.jamaMasjid,
    width: 735, height: 533,
    imageAlt: "Painting of Jama Masjid and the streets of Old Delhi, seen from a balcony",
    caption: "Jama Masjid, seen from a balcony. William Carpenter.",
  },
  {
    id: "qutub-complex",
    name: "Qutub Complex",
    title: "Monuments, myths & medieval Delhi.",
    description: "Look beyond the Qutub Minar to explore the layered history of one of Delhi's most fascinating archaeological landscapes.",
    image: ART.qutub,
    width: 736, height: 896,
    imageAlt: "Archival photograph of Qutub Minar and the surrounding archaeological ruins",
    caption: "Qutub Minar and its ruins. Supplied archival photograph.",
    detailImage: { src: ART.qutubWide, width: 640, height: 493, alt: "A wide archival view of the Qutub Complex, with Qutub Minar rising above the ruins", caption: "The Qutub Complex. Supplied archival photograph." },
  },
  {
    id: "nizamuddin",
    name: "Nizamuddin",
    title: "A neighbourhood of poetry, devotion & memory.",
    description: "Discover the world around Nizamuddin Dargah through Sufi traditions, architecture, music, poetry and the communities that continue to shape the neighbourhood.",
    image: ART.shrine,
    width: 736, height: 611,
    imageAlt: "Historical painting of visitors at a marble shrine, from the supplied artwork collection",
    caption: "A shrine study from the supplied artwork collection.",
  },
];

export const INTRO = [
  "Most tours of Old Delhi are narrated from the outside, by people reading from a script.",
  "Unzip Delhi is different.",
  "Founded by Anas Khan, an anthropologist and heritage storyteller, Unzip Delhi grew from a deeply personal connection to Shahjahanabad. His family has lived through the same lanes for more than 250 years, the very streets he now walks with travellers, journalists, diplomats and the simply curious.",
  "This is not sightseeing. It is storytelling rooted in scholarship, family memory and oral histories.",
  "Every walk brings together the rigour of research with the intimacy of someone showing you their own home, revealing not just what happened here, but how people have lived, remembered and reimagined the city across generations.",
  "We see Delhi not simply as a collection of historical sites, but as a living city where the past and present constantly meet.",
];

export const OFFERINGS = [
  { title: "Heritage Walks", text: "Our signature walking experiences through Old Delhi’s historic quarters, its temples, mosques, havelis and bustling bazaars, and beyond, to Sufi shrines carrying legacies of more than 700 years. We look beyond the monuments to uncover the histories, people and stories layered beneath everyday life." },
  { title: "Private & Custom Tours", text: "Tailored routes, pacing and experiences for individuals and small groups who want to explore the city at their own rhythm. From a first introduction to Delhi to deeply specialised journeys, we create experiences shaped around your interests and curiosity." },
  { title: "Institutional & Cultural Experiences", text: "Curated engagements for embassies, hotels, universities and cultural institutions seeking authentic, research-backed programming rooted in Delhi’s history, culture and living traditions." },
];

export const PRINCIPLES = [
  { title: "RESEARCH-LED", text: "Grounded in history, anthropology and years of research and documentation." },
  { title: "LOCALLY ROOTED", text: "Created from an intimate understanding of Delhi and its neighbourhoods, with Anas working alongside local residents, community members and people whose knowledge rarely makes it onto the tourist map. Our work also seeks to support and amplify communities and individuals who are often overlooked." },
  { title: "STORY-DRIVEN", text: "Because a place becomes memorable when you understand the people, memories and stories behind it, not just the monuments." },
  { title: "IMMERSIVE", text: "Walk, eat, listen, observe, question and experience the city as it is lived, rather than simply looking at it from the outside." },
  { title: "CURATED", text: "Every experience is thoughtfully designed, researched and shaped around a particular story, place or idea, not pulled from a standard itinerary." },
];

export const FOUNDER_ROLE = "Anthropologist · Heritage Storyteller · Founder, Unzip Delhi";
export const FOUNDER_STORY = [
  "Anas Khan comes from a family with a recorded history in Delhi spanning more than 250 years. Born and raised in Old Delhi, he grew up surrounded by the streets, stories and communities that continue to shape the city.",
  "An anthropologist and heritage storyteller, Anas has spent years researching, documenting and sharing the histories and cultures of Delhi, bringing together academic research, lived experience and the voices of the people who call these neighbourhoods home.",
  "Through Unzip Delhi, he offers a different way of encountering the city: curious rather than conventional, conversational rather than scripted, and deeply rooted in place.",
  "Because sometimes the best way to discover a city is to walk with someone who knows it from the inside.",
];

export const RECOGNITION = [
  "Unzip Delhi has been featured by leading publications including Vogue, Outlook Traveller, Condé Nast Traveller, and Travel + Leisure, who have turned to us to help tell the story of the Old City.",
  "Anas Khan has spoken about Delhi’s layered histories on the TEDx stage three times, taking stories rooted in its streets and neighbourhoods to audiences far beyond Delhi.",
  "Our work has also brought us into conversation with some of the world’s leading cultural, diplomatic and creative institutions and individuals. We have hosted and worked with the UNESCO World Heritage Committee, Air India, Apple, Google, Google Maps and Google Local Guides, alongside diplomats, cultural leaders and international visitors.",
  "Among those we have hosted are Pierpaolo Piccioli, Creative Director at Balenciaga; Dr. Philipp Ackermann, German Ambassador to India; and Dr. Piotr Antoni Świtalski, Polish Ambassador to India.",
  "We have also worked with the Embassy of the United States, Embassy of France, Embassy of Poland, and many other organisations seeking thoughtful, research-led ways to experience Delhi.",

  "We have also worked with leading hospitality brands across India, including Taj, ITC Hotels, Radisson and Hyatt, creating experiences that bring Delhi’s history and culture into meaningful conversations with guests from around the world.",
];

export const RECOGNITION_LOGOS = [
  { id: "publications", title: "Publications", logos: [
    { id: "vogue", name: "Vogue", image: "/recognition/vogue.svg" },
    { id: "outlook-traveller", name: "Outlook Traveller", image: "/recognition/outlook-traveller.png" },
    { id: "conde-nast", name: "Condé Nast Traveller", image: "/recognition/conde-nast.svg" },
    { id: "travel-leisure", name: "Travel + Leisure", image: "/recognition/travel-leisure.png" },
  ] },
  { id: "brands", title: "Brands & hospitality", logos: [
    { id: "apple", name: "Apple", image: "/recognition/apple.svg" },
    { id: "google", name: "Google", image: "/recognition/google.svg" },
    { id: "itc-hotels", name: "ITC Hotels", image: "/recognition/itc-hotels.svg" },
    { id: "radisson", name: "Radisson", image: "/recognition/radisson.png" },
    { id: "hyatt", name: "Hyatt", image: "/recognition/hyatt.png" },
  ] },
  { id: "embassies", title: "Embassies", logos: [
    { id: "us-embassy", name: "Embassy of the United States of America", image: "/recognition/united-states.png", alt: "United States national flag identifying the Embassy of the United States of America" },
    { id: "france", name: "Embassy of France", image: "/recognition/france.svg", alt: "French Republic Marianne mark used on the official French Embassy in India website" },
    { id: "poland", name: "Embassy of Poland", image: "/recognition/poland.svg", alt: "Republic of Poland eagle emblem from the official Polish diplomatic website" },
    { id: "germany", name: "Embassy of Germany", image: "/recognition/germany.svg", alt: "German federal eagle and national colours from the official German Missions in India website" },
  ] },
];
